/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import type AppService from 'embercom/services/app-service';
import Service from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import type { CompanySize } from 'embercom/components/expired-subscription/expired-subscription';
import { CompanySize as CompanySizeEnum } from 'embercom/components/expired-subscription/expired-subscription';
import { ExpirationReason } from 'embercom/components/expired-subscription/header/header-card';
import { showNewMessageInIntercomWidget } from 'embercom/lib/intercom-widget-helper';
import { action } from '@ember/object';
import type CardlessConversionModalService from './cardless-conversion-modal-service';
import { tracked } from '@glimmer/tracking';

export type ExpiredSubscriptionAnalyticsObject =
  | 'open_cardless_conversion_modal_button'
  | 'open_trial_extension_modal_button'
  | 'open_billing_settings_button'
  | 'open_messenger_fin_standalone_button'
  | 'redirect_to_checkout_button'
  | 'open_intercom_widget_button'
  | 'open_alert_billing_admin_modal_button'
  | 'apply_for_early_stage_button'
  | 'open_success_stories_button'
  | 'open_upcoming_features_button'
  | 'open_workspace_setup_guide_button'
  | 'open_about_fin_for_zendesk_button'
  | 'open_messenger_button';

export type ExpiredSubscriptionAnalyticsPlace =
  | 'expired_subscription_page_header_card'
  | 'expired_subscription_page_go_further_card';

export default class ExpiredSubscriptionService extends Service {
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare customerService: $TSFixMe;
  @service declare experimentOptInService: $TSFixMe;
  @service declare router: RouterService;
  @service declare cardlessConversionModalService: CardlessConversionModalService;
  @service declare purchaseAnalyticsService: $TSFixMe;

  @tracked shouldShowAlertBillingAdminModal = false;

  get expirationReason(): ExpirationReason | undefined {
    return Object.values(ExpirationReason).find(this.isExpirationReason, this);
  }

  private isExpirationReason(expirationReason: ExpirationReason) {
    return this.expirationReasonCriteria[expirationReason];
  }

  private get expirationReasonCriteria() {
    return {
      [ExpirationReason.NonPaymentSelfServe]: this.isNonPaymentSelfServe,
      [ExpirationReason.NonPaymentSalesLed]: this.isNonPaymentSalesLed,
      [ExpirationReason.FinStandaloneSalesLed]: this.showFinStandaloneSalesLedScreen,
      [ExpirationReason.FinStandaloneSelfServe]: this.showFinStandaloneSelfServeScreen,
      [ExpirationReason.ActiveEarlyStageApplication]: this.showActiveEarlyStageApplicationScreen,
      [ExpirationReason.FreeTrialEnded]: this.showTrialExtensionCTA,
      [ExpirationReason.SubscriptionCancelled]: this.showSubscriptionCancelledScreen,
      [ExpirationReason.NewWorkspace]: this.isNewWorkspace,
    };
  }

  private get showActiveEarlyStageApplicationScreen() {
    return this.appService.app.validEarlyStageApplicant;
  }

  private get showFinStandaloneSalesLedScreen() {
    return this.isFinStandalone && !this.everInCardlessTrial;
  }

  private get showFinStandaloneSelfServeScreen() {
    return this.isFinStandalone && this.everInCardlessTrial;
  }

  private get isFinStandalone() {
    return this.appService.app.isStandaloneApp;
  }

  private get everInCardlessTrial() {
    return this.customerService.customer.everInCardlessTrial;
  }

  private get showTrialExtensionCTA() {
    return !!(
      this.customerService.customer.inExpiredCardlessTrial &&
      this.appService.app.teamPurchaseExperienceTrialExtensionsVariant
    );
  }

  private get showSubscriptionCancelledScreen() {
    return !!(
      this.customerService.customer.subscriptionWasCancelled &&
      !this.customerService.customer.inExpiredTrial &&
      !this.appService.app.isFrozenForNonPayment
    );
  }

  private get isNonPaymentSelfServe() {
    return !!(
      this.appService.app.isFrozenForNonPayment &&
      !this.customerService.customer.subscription?.isSalesforceContracted
    );
  }

  private get isNonPaymentSalesLed() {
    return !!(
      this.appService.app.isFrozenForNonPayment &&
      this.customerService.customer.subscription?.isSalesforceContracted
    );
  }

  private get isNewWorkspace() {
    return (
      !this.customerService.customer.hasEverHadSubscription &&
      !this.customerService.customer.everInCardlessTrial
    );
  }

  get adminHasBillingPermissions(): boolean {
    return !!this.permissionsService.currentAdminCan('can_access_billing_settings');
  }

  get companySize(): CompanySize {
    switch (this.appService.app.companySize) {
      case '6-20':
      case '10-49':
      case '11-49':
      case '11-100':
      case '16-49':
      case '21-50':
        return CompanySizeEnum.Medium;
      case '50-199':
      case '51-150':
      case '101-500':
      case '150-300':
      case '200-499':
      case '200-999':
      case '200-1,999':
      case '301-1,000':
      case '500-999':
      case '501-2000':
      case '1,000+':
      case '1,001-5,000':
      case '2,000-4,999':
      case '2,001+':
      case '5,000+':
      case '5,001+':
        return CompanySizeEnum.Large;
      case '1-4':
      case '1-5':
      case '1-9':
      case '1-10':
      case '1-15':
      case '5-9':
      case '6-10':
      case '6-15':
        return CompanySizeEnum.Small;
      default:
        return CompanySizeEnum.Medium;
    }
  }

  get hasActiveSubscription() {
    return !!this.appService.app.hasActiveSubscription;
  }

  @action
  sendAnalyticsEvent({
    place,
    object,
    extra,
  }: {
    place: ExpiredSubscriptionAnalyticsPlace;
    object: ExpiredSubscriptionAnalyticsObject;
    extra?: any;
  }) {
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      context: 'expired_subscription',
      object,
      place,
      extra: {
        expiration_reason: this.expirationReason ?? 'default',
        is_billing_admin: this.adminHasBillingPermissions,
        company_size: this.companySize,
        ...extra,
      },
    });
  }

  @action
  openCardlessConversionModal() {
    this.cardlessConversionModalService.setCustomerWantsToExtendTrial(false);
    this.cardlessConversionModalService.toggle();
  }

  @action
  openTrialExtensionModal() {
    this.cardlessConversionModalService.setCustomerWantsToExtendTrial(true);
    this.cardlessConversionModalService.toggle();
  }

  @action
  transitionToBillingSettings() {
    this.router.transitionTo('apps.app.settings.subscription.billing.settings');
  }

  @action
  openMessenger() {
    showNewMessageInIntercomWidget();
  }

  @action
  redirectToCheckout() {
    this.router.transitionTo('apps.app.teams-checkout', this.appService.app.id);
  }

  @action
  toggleAlertBillingAdminModal() {
    this.shouldShowAlertBillingAdminModal = !this.shouldShowAlertBillingAdminModal;
  }
}

declare module '@ember/service' {
  interface Registry {
    'expired-subscription-service': ExpiredSubscriptionService;
  }
}
